import type { CSSProperties } from 'react';
import { cn } from '~/lib/utils';
import styles from './protege-loader.module.css';

export type ProtegeLoaderSize = 'sm' | 'md' | 'lg';

export const PROTEGE_LOADER_DOT_GRID_SIZE = 12;
export const PROTEGE_LOADER_DOT_COUNT =
  PROTEGE_LOADER_DOT_GRID_SIZE * PROTEGE_LOADER_DOT_GRID_SIZE;

const PROTEGE_LOADER_REFERENCE_GRID_SIZE = 9;
const PROTEGE_LOADER_REFERENCE_COLUMN_STAGGER_MS = 70;
const PROTEGE_LOADER_REFERENCE_ROW_STAGGER_MS = 12;
const PROTEGE_LOADER_WAVE_DURATION_MS = 1350;

const protegeLoaderGridSpan = PROTEGE_LOADER_DOT_GRID_SIZE - 1;
const protegeLoaderReferenceGridSpan = PROTEGE_LOADER_REFERENCE_GRID_SIZE - 1;

export const PROTEGE_LOADER_COLUMN_STAGGER_MS = Math.round(
  (PROTEGE_LOADER_REFERENCE_COLUMN_STAGGER_MS * protegeLoaderReferenceGridSpan) /
    protegeLoaderGridSpan,
);
export const PROTEGE_LOADER_ROW_STAGGER_MS = Math.round(
  (PROTEGE_LOADER_REFERENCE_ROW_STAGGER_MS * protegeLoaderReferenceGridSpan) /
    protegeLoaderGridSpan,
);

const sizeClassName: Record<ProtegeLoaderSize, string | undefined> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

function protegeLoaderDotGridTemplate(size: number) {
  return `repeat(${size}, 1fr)`;
}

function protegeLoaderDotDelayMs(index: number) {
  const col = index % PROTEGE_LOADER_DOT_GRID_SIZE;
  const row = Math.floor(index / PROTEGE_LOADER_DOT_GRID_SIZE);
  const baseDelayMs =
    col * PROTEGE_LOADER_COLUMN_STAGGER_MS + row * PROTEGE_LOADER_ROW_STAGGER_MS;
  return baseDelayMs % PROTEGE_LOADER_WAVE_DURATION_MS;
}

function ProtegeLoaderDotGrid() {
  const gridTemplate = protegeLoaderDotGridTemplate(PROTEGE_LOADER_DOT_GRID_SIZE);

  return (
    <div
      className={styles.dots}
      style={
        {
          gridTemplateColumns: gridTemplate,
          gridTemplateRows: gridTemplate,
        } as CSSProperties
      }
    >
      {Array.from({ length: PROTEGE_LOADER_DOT_COUNT }, (_, index) => (
        <span
          key={index}
          className={cn(styles.dot, 'rounded-full')}
          style={
            {
              '--protege-loader-dot-delay': `${protegeLoaderDotDelayMs(index)}ms`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

export function ProtegeLoader({
  size = 'md',
  label,
  className,
  'aria-label': ariaLabel,
}: {
  size?: ProtegeLoaderSize;
  label?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const accessibleLabel = ariaLabel ?? label ?? 'Loading';

  return (
    <div
      className={cn(styles.loader, sizeClassName[size], className)}
      role="status"
      aria-live="polite"
      aria-label={accessibleLabel}
    >
      <div className={styles.mask} aria-hidden="true">
        <ProtegeLoaderDotGrid />
      </div>
      <div className={styles.reduced} aria-hidden="true" />
    </div>
  );
}
